// // screens/marketplace/MarketplacePricing.jsx
// import React, { useCallback, useEffect, useMemo, useState } from "react";
// import {
//   SlidersHorizontal,
//   Plus,
//   Trash2,
//   Pencil,
//   X,
//   Package as PackageIcon,
//   Users,
//   ShoppingBag,
//   Clock,
//   AlertCircle,
//   Info,
//   Save,
//   Search,
//   RefreshCw,
//   Layers,
//   Globe,
// } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import { useAuth } from "../../providers/AuthProvider";
// import { useTheme } from "../../hooks/useTheme";
// import { useToast } from "../../providers/ToastProvider";
// import {
//   PRICE_TYPES,
//   availablePriceTypes,
//   priceTypeMeta,
//   variantKeyLabel,
//   listPricing,
//   savePricing,
//   updatePricing,
//   deletePricing,
// } from "./pricingApi";

// /* ══════════════════════════════════════════════════════════
//    Pricing workspace for agents and sub-agents.

//    Two contexts, driven by the backend's price-type matrix:
//      • "customer"  → what walk-in buyers pay on my storefront
//      • "subAgent"  → what my sub-agents pay as their cost
//                     (agent-only; sub-agents can't set their own cost)

//    Variant awareness:
//      • Each product row groups its overrides by variant_key.
//      • "All variants" (variant_key === "") shows first, then one row
//        per variant that has an override.
//      • The modal lets you pick a scope — product-level or a specific
//        variant — and remembers the choice.

//    Because POST /pricing/ does update-or-create keyed on
//    (product, variant_key, user, price_type), the form never needs
//    to know in advance whether a row exists — it just posts.
//    ══════════════════════════════════════════════════════════ */

// const CONTEXTS = [
//   {
//     id: "customer",
//     title: "My customers",
//     description: "Prices your walk-in buyers see on your storefront.",
//     icon: ShoppingBag,
//   },
//   {
//     id: "subAgent",
//     title: "My sub-agents",
//     description: "Costs your sub-agents see when they resell.",
//     icon: Users,
//   },
// ];

// export default function MarketplacePricing() {
//   const { authFetch, user } = useAuth();
//   const { colors } = useTheme();
//   const { showToast } = useToast();
//   const navigate = useNavigate();

//   const role = user?.role || "AGENT";
//   const canSeeSubAgentContext = role === "ADMIN" || role === "AGENT";

//   const [context, setContext] = useState("customer");
//   const [loading, setLoading] = useState(true);
//   const [refreshing, setRefreshing] = useState(false);

//   const [products, setProducts] = useState([]);
//   const [subAgents, setSubAgents] = useState([]);
//   const [overrides, setOverrides] = useState([]);

//   const [search, setSearch] = useState("");
//   const [modal, setModal] = useState(null); // { mode, row?, context, productId? }
//   const [busyId, setBusyId] = useState(null);

//   // ── Load ────────────────────────────────────────────────
//   const load = useCallback(
//     async ({ silent } = {}) => {
//       if (!silent) setLoading(true);
//       else setRefreshing(true);
//       try {
//         const fetches = [
//           authFetch("/marketplace/products/"),
//           authFetch("/marketplace/pricing/"),
//         ];
//         if (canSeeSubAgentContext) {
//           fetches.push(authFetch("/accounts/agent/sub-agents/"));
//         }
//         const results = await Promise.all(fetches);
//         const bodies = await Promise.all(results.map((r) => r.json()));

//         if (results[0].ok) {
//           setProducts(
//             (bodies[0].data || []).map((p) => ({
//               id: p.id,
//               name: p.name,
//               sku: p.sku || p.slug,
//               basePriceAgent: parseFloat(p.base_price_agent || 0),
//               basePriceCustomer: parseFloat(p.base_price_customer || 0),
//               preOrderBaseAgent: parseFloat(
//                 p.pre_order_price_agent || p.base_price_agent || 0
//               ),
//               preOrderBaseCustomer: parseFloat(
//                 p.pre_order_price_customer || p.base_price_customer || 0
//               ),
//               image:
//                 p.thumbnail_image_url ||
//                 p.main_image_url ||
//                 p.main_image ||
//                 null,
//               saleType: p.sale_type,
//               hasVariants: !!p.has_variants,
//               variantAttributes: Array.isArray(p.variant_attributes)
//                 ? p.variant_attributes
//                 : [],
//               variants: Array.isArray(p.variants)
//                 ? p.variants.map((v) => ({
//                     key: v.key,
//                     attributes: v.attributes || {},
//                     stock: v.stock != null ? Number(v.stock) : null,
//                     basePriceAgent: parseFloat(v.base_price_agent || 0),
//                     basePriceCustomer: parseFloat(
//                       v.base_price_customer || 0
//                     ),
//                     preOrderBaseAgent: parseFloat(
//                       v.pre_order_price_agent || v.base_price_agent || 0
//                     ),
//                     preOrderBaseCustomer: parseFloat(
//                       v.pre_order_price_customer ||
//                         v.base_price_customer ||
//                         0
//                     ),
//                   }))
//                 : [],
//             }))
//           );
//         }
//         if (results[1].ok) setOverrides(bodies[1].data || []);
//         if (canSeeSubAgentContext && results[2]?.ok) {
//           setSubAgents(bodies[2].data?.sub_agents || []);
//         }
//       } catch (err) {
//         console.error(err);
//         showToast("Could not load pricing", "error");
//       } finally {
//         setLoading(false);
//         setRefreshing(false);
//       }
//     },
//     [authFetch, canSeeSubAgentContext, showToast]
//   );

//   useEffect(() => {
//     load();
//   }, [load]);

//   // ── Derived ─────────────────────────────────────────────
//   const overridesByProduct = useMemo(() => {
//     const map = new Map();
//     overrides.forEach((o) => {
//       if (!map.has(o.product)) map.set(o.product, []);
//       map.get(o.product).push(o);
//     });
//     return map;
//   }, [overrides]);

//   const rows = useMemo(() => {
//     const q = search.trim().toLowerCase();
//     const filtered = q
//       ? products.filter((p) => p.name.toLowerCase().includes(q))
//       : products;

//     return filtered.map((product) => {
//       const productOverrides = overridesByProduct.get(product.id) || [];
//       return { product, overrides: productOverrides };
//     });
//   }, [products, overridesByProduct, search]);

//   // ── Actions ─────────────────────────────────────────────
//   const handleSave = async (payload) => {
//     try {
//       const saved = await savePricing(authFetch, payload);
//       // Optimistic merge keyed on (product, variant_key, user, price_type)
//       setOverrides((prev) => {
//         const key = (o) =>
//           `${o.product}:${o.variant_key || ""}:${o.user}:${o.price_type}`;
//         const savedKey = key(saved);
//         const without = prev.filter((o) => key(o) !== savedKey);
//         return [...without, saved];
//       });
//       showToast("Price saved", "success");
//       setModal(null);
//     } catch (err) {
//       showToast(err.message, "error");
//       throw err;
//     }
//   };

//   const handleDelete = async (row) => {
//     const scope = row.variant_key
//       ? `the ${variantKeyLabel(row.variant_key)} variant`
//       : "all variants";
//     if (
//       !window.confirm(
//         `Remove the ${priceTypeMeta(row.price_type).label} override for ${scope}? The fallback price will apply.`
//       )
//     )
//       return;
//     setBusyId(row.id);
//     try {
//       await deletePricing(authFetch, row.id);
//       setOverrides((prev) => prev.filter((o) => o.id !== row.id));
//       showToast("Override removed", "success");
//     } catch (err) {
//       showToast(err.message, "error");
//     } finally {
//       setBusyId(null);
//     }
//   };

//   // ── Render ──────────────────────────────────────────────
//   if (loading) {
//     return (
//       <div style={styles.loadingWrap(colors)}>
//         <div style={styles.spinner(colors)} />
//         <span style={{ fontSize: 13, color: colors.secondaryText }}>
//           Loading pricing workspace…
//         </span>
//       </div>
//     );
//   }

//   return (
//     <div style={styles.pageWrap}>
//       {/* Header */}
//       <header style={styles.header}>
//         <div style={{ minWidth: 0 }}>
//           <h1 style={styles.h1(colors)}>
//             <SlidersHorizontal size={22} color={colors.btnBg} />
//             Store pricing
//           </h1>
//           <p style={styles.subtitle(colors)}>
//             Control what your customers and sub-agents pay. Every price falls
//             back to the admin base if you don't set an override.
//           </p>
//         </div>
//         <div style={{ display: "flex", gap: 8 }}>
//           <button
//             type="button"
//             onClick={() => load({ silent: true })}
//             disabled={refreshing}
//             style={styles.ghostBtn(colors)}
//           >
//             <RefreshCw
//               size={14}
//               style={{
//                 animation: refreshing ? "spin 1s linear infinite" : "none",
//               }}
//             />
//             Refresh
//           </button>
//           <button
//             type="button"
//             onClick={() => setModal({ mode: "create", context })}
//             style={styles.primaryBtn(colors, false)}
//           >
//             <Plus size={14} />
//             Add price
//           </button>
//         </div>
//       </header>

//       {/* Context tabs */}
//       {canSeeSubAgentContext && (
//         <div style={styles.contextTabs(colors)}>
//           {CONTEXTS.map((c) => {
//             const Icon = c.icon;
//             const active = context === c.id;
//             return (
//               <button
//                 key={c.id}
//                 type="button"
//                 onClick={() => setContext(c.id)}
//                 style={styles.contextTab(colors, active)}
//               >
//                 <span style={styles.contextTabIcon(colors, active)}>
//                   <Icon size={15} />
//                 </span>
//                 <span style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
//                   <span
//                     style={{
//                       display: "block",
//                       fontSize: 13.5,
//                       fontWeight: 700,
//                       color: active ? colors.heading : colors.text,
//                     }}
//                   >
//                     {c.title}
//                   </span>
//                   <span
//                     style={{
//                       display: "block",
//                       fontSize: 11.5,
//                       color: colors.secondaryText,
//                       marginTop: 2,
//                     }}
//                   >
//                     {c.description}
//                   </span>
//                 </span>
//               </button>
//             );
//           })}
//         </div>
//       )}

//       {/* Search */}
//       <div style={styles.toolbar}>
//         <div style={styles.searchWrap}>
//           <Search size={14} color={colors.secondaryText} style={styles.searchIcon} />
//           <input
//             value={search}
//             onChange={(e) => setSearch(e.target.value)}
//             placeholder="Search products…"
//             style={styles.searchInput(colors)}
//           />
//           {search && (
//             <button
//               type="button"
//               onClick={() => setSearch("")}
//               style={styles.searchClear}
//               aria-label="Clear"
//             >
//               <X size={13} />
//             </button>
//           )}
//         </div>
//         <span style={styles.resultCount(colors)}>
//           {rows.length} product{rows.length === 1 ? "" : "s"}
//         </span>
//       </div>

//       {/* Rows */}
//       {rows.length === 0 ? (
//         <EmptyState colors={colors} search={search} />
//       ) : (
//         <div style={styles.listWrap}>
//           {rows.map(({ product, overrides }) => (
//             <ProductPricingRow
//               key={product.id}
//               product={product}
//               overrides={overrides}
//               context={context}
//               role={role}
//               subAgents={subAgents}
//               colors={colors}
//               onAdd={(variantKey) =>
//                 setModal({
//                   mode: "create",
//                   context,
//                   productId: product.id,
//                   variantKey,
//                 })
//               }
//               onEdit={(row) => setModal({ mode: "edit", row, context })}
//               onDelete={handleDelete}
//               busyId={busyId}
//             />
//           ))}
//         </div>
//       )}

//       {/* Modal */}
//       {modal && (
//         <PriceEditorModal
//           mode={modal.mode}
//           initial={modal.row}
//           context={modal.context}
//           role={role}
//           products={products}
//           subAgents={subAgents}
//           presetProductId={modal.productId}
//           presetVariantKey={modal.variantKey}
//           colors={colors}
//           onClose={() => setModal(null)}
//           onSave={handleSave}
//           showToast={showToast}
//         />
//       )}

//       <style>{`
//         @keyframes spin { to { transform: rotate(360deg); } }
//         @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
//         @keyframes slideUp {
//           from { opacity: 0; transform: translateY(16px); }
//           to { opacity: 1; transform: translateY(0); }
//         }
//       `}</style>
//     </div>
//   );
// }

// /* ══════════════════════════════════════════════════════════
//    Product row — grouped by variant
//    ══════════════════════════════════════════════════════════ */

// const ProductPricingRow = ({
//   product,
//   overrides,
//   context,
//   role,
//   subAgents,
//   colors,
//   onAdd,
//   onEdit,
//   onDelete,
//   busyId,
// }) => {
//   // Filter overrides by context (customer-facing vs sub-agent cost)
//   const contextOverrides = overrides.filter((o) => {
//     const meta = priceTypeMeta(o.price_type);
//     if (context === "customer") return meta.scope === "customer-facing";
//     return (
//       meta.scope === "sub-agent-cost" ||
//       meta.scope === "sub-agent-cost-override"
//     );
//   });

//   // Group by variant key. "" is the product-level bucket.
//   const byVariant = useMemo(() => {
//     const map = new Map();
//     contextOverrides.forEach((o) => {
//       const key = o.variant_key || "";
//       if (!map.has(key)) map.set(key, []);
//       map.get(key).push(o);
//     });
//     return map;
//   }, [contextOverrides]);

//   const hasVariants = product.hasVariants && product.variants.length > 0;

//   // Product-level summary line
//   const productLevel = byVariant.get("") || [];
//   const customerPriceAtProduct = productLevel.find(
//     (o) => o.price_type === "AGENT_CUSTOMER_PRICE"
//   );
//   const subBaseAtProduct = productLevel.find(
//     (o) => o.price_type === "AGENT_SUB_BASE_PRICE"
//   );

//   // Variant keys to display as sub-rows
//   const variantKeysToShow = useMemo(() => {
//     if (!hasVariants) return [];
//     // Union of: variants defined on the product + variant keys with overrides
//     const defined = product.variants.map((v) => v.key);
//     const withOverrides = [...byVariant.keys()].filter((k) => k !== "");
//     const seen = new Set();
//     return [...defined, ...withOverrides].filter((k) => {
//       if (!k || seen.has(k)) return false;
//       seen.add(k);
//       return true;
//     });
//   }, [hasVariants, product.variants, byVariant]);

//   return (
//     <div style={styles.row(colors)}>
//       <div style={styles.rowTop}>
//         <div style={styles.rowIdentity}>
//           <div style={styles.thumb(colors)}>
//             {product.image ? (
//               <img
//                 src={product.image}
//                 alt=""
//                 style={{ width: "100%", height: "100%", objectFit: "cover" }}
//               />
//             ) : (
//               <PackageIcon size={20} color={colors.secondaryText} />
//             )}
//           </div>
//           <div style={{ minWidth: 0 }}>
//             <div style={styles.rowName(colors)}>
//               {product.name}
//               {hasVariants && (
//                 <span
//                   style={{
//                     marginLeft: 8,
//                     fontSize: 10,
//                     fontWeight: 700,
//                     padding: "2px 7px",
//                     borderRadius: 999,
//                     background: colors.hoverBg,
//                     color: colors.secondaryText,
//                     letterSpacing: 0.3,
//                     textTransform: "uppercase",
//                   }}
//                 >
//                   {product.variants.length} variants
//                 </span>
//               )}
//             </div>
//             <div style={styles.rowMeta(colors)}>
//               Base agent ₵{product.basePriceAgent.toFixed(2)}
//               {context === "customer" && customerPriceAtProduct && (
//                 <>
//                   {" · "}
//                   Customer ₵
//                   {parseFloat(customerPriceAtProduct.price).toFixed(2)}
//                 </>
//               )}
//               {context === "subAgent" && subBaseAtProduct && (
//                 <>
//                   {" · "}
//                   Sub-agent base ₵
//                   {parseFloat(subBaseAtProduct.price).toFixed(2)}
//                 </>
//               )}
//             </div>
//           </div>
//         </div>
//         <button
//           type="button"
//           onClick={() => onAdd("")}
//           style={styles.addRowBtn(colors)}
//           title="Add override"
//         >
//           <Plus size={14} />
//         </button>
//       </div>

//       {/* Product-level overrides */}
//       {productLevel.length > 0 && (
//         <ChipGroup
//           label="All variants"
//           icon={Globe}
//           overrides={productLevel}
//           subAgents={subAgents}
//           colors={colors}
//           onEdit={onEdit}
//           onDelete={onDelete}
//           busyId={busyId}
//         />
//       )}

//       {/* Per-variant overrides */}
//       {hasVariants &&
//         variantKeysToShow.map((vKey) => {
//           const vOverrides = byVariant.get(vKey) || [];
//           const variantMeta = product.variants.find((v) => v.key === vKey);
//           return (
//             <ChipGroup
//               key={vKey}
//               label={variantKeyLabel(vKey)}
//               sublabel={
//                 variantMeta
//                   ? `base ₵${variantMeta.basePriceAgent.toFixed(2)}`
//                   : null
//               }
//               icon={Layers}
//               overrides={vOverrides}
//               subAgents={subAgents}
//               colors={colors}
//               onAdd={() => onAdd(vKey)}
//               onEdit={onEdit}
//               onDelete={onDelete}
//               busyId={busyId}
//             />
//           );
//         })}
//     </div>
//   );
// };

// /* ══════════════════════════════════════════════════════════
//    Chip group — one row per variant scope
//    ══════════════════════════════════════════════════════════ */

// const ChipGroup = ({
//   label,
//   sublabel,
//   icon: Icon,
//   overrides,
//   subAgents,
//   colors,
//   onAdd,
//   onEdit,
//   onDelete,
//   busyId,
// }) => {
//   return (
//     <div style={styles.chipGroup(colors)}>
//       <div style={styles.chipGroupHeader}>
//         <div style={styles.chipGroupLabel(colors)}>
//           {Icon && <Icon size={11} />}
//           {label}
//           {sublabel && (
//             <span style={styles.chipGroupSublabel(colors)}> · {sublabel}</span>
//           )}
//         </div>
//         {onAdd && (
//           <button
//             type="button"
//             onClick={onAdd}
//             style={styles.chipGroupAdd(colors)}
//             title="Add override for this variant"
//           >
//             <Plus size={11} />
//           </button>
//         )}
//       </div>
//       {overrides.length === 0 ? (
//         <span style={styles.chipGroupEmpty(colors)}>No override</span>
//       ) : (
//         <div style={styles.chipsWrap}>
//           {overrides.map((o) => {
//             const meta = priceTypeMeta(o.price_type);
//             const subName =
//               meta.scope === "sub-agent-cost-override"
//                 ? subAgents.find((s) => s.user_id === o.user)?.username ||
//                   o.username
//                 : null;
//             const busy = busyId === o.id;
//             return (
//               <span key={o.id} style={styles.chip(colors, meta.isPreorder)}>
//                 <span style={styles.chipLabel(colors)}>
//                   {meta.isPreorder ? <Clock size={11} /> : <Info size={11} />}
//                   {subName ? `${meta.label} · @${subName}` : meta.label}
//                 </span>
//                 <span style={styles.chipValue(colors)}>
//                   ₵{parseFloat(o.price).toFixed(2)}
//                 </span>
//                 <button
//                   type="button"
//                   onClick={() => onEdit(o)}
//                   disabled={busy}
//                   style={styles.chipIconBtn(colors)}
//                   title="Edit"
//                 >
//                   <Pencil size={11} />
//                 </button>
//                 <button
//                   type="button"
//                   onClick={() => onDelete(o)}
//                   disabled={busy}
//                   style={styles.chipIconBtn(colors, true)}
//                   title="Remove"
//                 >
//                   {busy ? (
//                     <RefreshCw
//                       size={11}
//                       style={{ animation: "spin 0.9s linear infinite" }}
//                     />
//                   ) : (
//                     <Trash2 size={11} />
//                   )}
//                 </button>
//               </span>
//             );
//           })}
//         </div>
//       )}
//     </div>
//   );
// };

// /* ══════════════════════════════════════════════════════════
//    Price editor modal
//    ══════════════════════════════════════════════════════════ */

// const PriceEditorModal = ({
//   mode,
//   initial,
//   context,
//   role,
//   products,
//   subAgents,
//   presetProductId,
//   presetVariantKey,
//   colors,
//   onClose,
//   onSave,
// }) => {
//   const isEdit = mode === "edit";

//   const availableTypes = useMemo(
//     () => availablePriceTypes(role, context),
//     [role, context]
//   );

//   const [product, setProduct] = useState(
//     initial?.product || presetProductId || ""
//   );
//   const [variantKey, setVariantKey] = useState(
//     initial?.variant_key || presetVariantKey || ""
//   );
//   const [targetUser, setTargetUser] = useState(initial?.user || "");
//   const [priceType, setPriceType] = useState(
//     initial?.price_type || availableTypes[0]?.value || ""
//   );
//   const [price, setPrice] = useState(initial?.price || "");
//   const [notes, setNotes] = useState(initial?.notes || "");
//   const [submitting, setSubmitting] = useState(false);
//   const [fieldErrors, setFieldErrors] = useState({});

//   const selectedProduct = products.find((p) => p.id === product);
//   const productVariants = selectedProduct?.variants || [];

//   // The sub-agent picker is only relevant when the price type targets
//   // a specific sub-agent (AGENT_SUB_CUSTOM_*), and only when the caller
//   // is an agent or admin.
//   const needsSubAgentPicker =
//     !isEdit &&
//     (priceType === PRICE_TYPES.AGENT_SUB_CUSTOM_PRICE.value ||
//       priceType ===
//         PRICE_TYPES.AGENT_SUB_PRE_ORDER_CUSTOM_PRICE.value) &&
//     (role === "AGENT" || role === "ADMIN");

//   // Fallback price shown to the user. Uses variant base if a variant
//   // is picked, else the product-level base.
//   const fallbackPrice = useMemo(() => {
//     if (!selectedProduct) return null;
//     const isPreorder = priceTypeMeta(priceType).isPreorder;
//     if (variantKey) {
//       const v = productVariants.find((x) => x.key === variantKey);
//       if (v) {
//         return isPreorder ? v.preOrderBaseAgent : v.basePriceAgent;
//       }
//     }
//     return isPreorder
//       ? selectedProduct.preOrderBaseAgent
//       : selectedProduct.basePriceAgent;
//   }, [selectedProduct, productVariants, variantKey, priceType]);

//   const submit = async (e) => {
//     e.preventDefault();
//     setFieldErrors({});

//     const payload = {
//       product,
//       variant_key: variantKey || "",
//       price_type: priceType,
//       price,
//       notes,
//     };

//     if (!isEdit) {
//       if (needsSubAgentPicker) {
//         if (!targetUser) {
//           setFieldErrors({ targetUser: "Select a sub-agent" });
//           return;
//         }
//         payload.user = targetUser;
//       }
//       // For everything else, backend derives user from the caller.
//     }

//     setSubmitting(true);
//     try {
//       await onSave(payload);
//     } catch (err) {
//       setFieldErrors({ _general: err.message });
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   return (
//     <div
//       onClick={(e) => e.target === e.currentTarget && onClose()}
//       style={styles.modalOverlay(colors)}
//     >
//       <form onSubmit={submit} style={styles.modalCard(colors)}>
//         <header style={styles.modalHeader}>
//           <div>
//             <div style={styles.modalTitle(colors)}>
//               {isEdit ? "Edit price override" : "Add price override"}
//             </div>
//             <div style={styles.modalSubtitle(colors)}>
//               {context === "customer"
//                 ? "What your customers pay"
//                 : "What your sub-agents pay"}
//             </div>
//           </div>
//           <button
//             type="button"
//             onClick={onClose}
//             style={styles.closeBtn(colors)}
//             aria-label="Close"
//           >
//             <X size={16} />
//           </button>
//         </header>

//         <div style={styles.modalBody}>
//           {!isEdit && (
//             <Field label="Product" colors={colors}>
//               <select
//                 required
//                 value={product}
//                 onChange={(e) => {
//                   setProduct(e.target.value);
//                   setVariantKey("");
//                 }}
//                 style={styles.select(colors)}
//               >
//                 <option value="">Choose product</option>
//                 {products.map((p) => (
//                   <option key={p.id} value={p.id}>
//                     {p.name}
//                   </option>
//                 ))}
//               </select>
//             </Field>
//           )}

//           {isEdit && selectedProduct ? (
//             <div style={styles.previewBox(colors)}>
//               <div style={styles.previewLabel(colors)}>Product</div>
//               <div style={styles.previewValue(colors)}>
//                 {initial.product_name || selectedProduct.name}
//               </div>
//             </div>
//           ) : null}

//           {/* Variant scope picker */}
//           {!isEdit && selectedProduct && productVariants.length > 0 && (
//             <Field
//               label="Applies to"
//               colors={colors}
//               hint="Product-level overrides apply when a variant has no override of its own."
//             >
//               <select
//                 value={variantKey}
//                 onChange={(e) => setVariantKey(e.target.value)}
//                 style={styles.select(colors)}
//               >
//                 <option value="">All variants (product-level)</option>
//                 {productVariants.map((v) => (
//                   <option key={v.key} value={v.key}>
//                     {variantKeyLabel(v.key)}
//                   </option>
//                 ))}
//               </select>
//             </Field>
//           )}

//           {isEdit && initial?.variant_key && (
//             <div style={styles.previewBox(colors)}>
//               <div style={styles.previewLabel(colors)}>Applies to</div>
//               <div style={styles.previewValue(colors)}>
//                 {variantKeyLabel(initial.variant_key)}
//               </div>
//             </div>
//           )}

//           <Field label="Price type" colors={colors}>
//             <select
//               required
//               disabled={isEdit}
//               value={priceType}
//               onChange={(e) => setPriceType(e.target.value)}
//               style={styles.select(colors)}
//             >
//               {availableTypes.map((t) => (
//                 <option key={t.value} value={t.value}>
//                   {t.label}
//                 </option>
//               ))}
//             </select>
//           </Field>

//           {needsSubAgentPicker && (
//             <Field
//               label="Target sub-agent"
//               colors={colors}
//               error={fieldErrors.targetUser}
//             >
//               <select
//                 required
//                 value={targetUser}
//                 onChange={(e) => setTargetUser(e.target.value)}
//                 style={styles.select(colors)}
//               >
//                 <option value="">Choose sub-agent</option>
//                 {subAgents.map((s) => (
//                   <option key={s.user_id} value={s.user_id}>
//                     {s.username || s.full_name || s.user_id}
//                   </option>
//                 ))}
//               </select>
//             </Field>
//           )}

//           {fallbackPrice != null && (
//             <div style={styles.previewBox(colors)}>
//               <div style={styles.previewLabel(colors)}>
//                 Fallback price if left empty
//               </div>
//               <div style={styles.previewValue(colors)}>
//                 ₵{fallbackPrice.toFixed(2)}
//               </div>
//               <div style={styles.previewHint(colors)}>
//                 Your price must be at least this much.
//               </div>
//             </div>
//           )}

//           <Field
//             label="Your price"
//             colors={colors}
//             error={fieldErrors.price}
//           >
//             <div style={styles.inputPrefixWrap}>
//               <span style={styles.inputPrefix(colors)}>₵</span>
//               <input
//                 type="number"
//                 required
//                 step="0.01"
//                 min="0"
//                 value={price}
//                 onChange={(e) => setPrice(e.target.value)}
//                 style={styles.inputWithPrefix(colors)}
//               />
//             </div>
//           </Field>

//           <Field label="Notes (optional)" colors={colors}>
//             <input
//               type="text"
//               value={notes}
//               onChange={(e) => setNotes(e.target.value)}
//               placeholder="Why this price?"
//               style={styles.input(colors)}
//             />
//           </Field>

//           {fieldErrors._general && (
//             <div style={styles.errorBox(colors)}>
//               <AlertCircle size={14} />
//               {fieldErrors._general}
//             </div>
//           )}
//         </div>

//         <footer style={styles.modalFooter}>
//           <button
//             type="button"
//             onClick={onClose}
//             style={styles.secondaryBtn(colors)}
//           >
//             Cancel
//           </button>
//           <button
//             type="submit"
//             disabled={submitting}
//             style={styles.primaryBtn(colors, submitting)}
//           >
//             {submitting ? (
//               <>
//                 <RefreshCw
//                   size={13}
//                   style={{ animation: "spin 0.9s linear infinite" }}
//                 />
//                 Saving…
//               </>
//             ) : (
//               <>
//                 <Save size={14} /> Save
//               </>
//             )}
//           </button>
//         </footer>
//       </form>
//     </div>
//   );
// };

// /* ══════════════════════════════════════════════════════════
//    Small components
//    ══════════════════════════════════════════════════════════ */

// const Field = ({ label, children, colors, error, hint }) => (
//   <label style={{ display: "block" }}>
//     <span style={styles.fieldLabel(colors)}>{label}</span>
//     {children}
//     {hint && !error && (
//       <span style={styles.fieldHint(colors)}>{hint}</span>
//     )}
//     {error && (
//       <span style={styles.fieldError(colors)}>
//         <AlertCircle size={11} /> {error}
//       </span>
//     )}
//   </label>
// );

// const EmptyState = ({ colors, search }) => (
//   <div style={styles.emptyState(colors)}>
//     <div style={styles.emptyIcon(colors)}>
//       <SlidersHorizontal size={24} />
//     </div>
//     <h3 style={styles.emptyTitle(colors)}>
//       {search ? "No matches" : "No products yet"}
//     </h3>
//     <p style={styles.emptyText(colors)}>
//       {search
//         ? "Try a different search term."
//         : "Products will appear here once the catalogue is populated."}
//     </p>
//   </div>
// );

// /* ══════════════════════════════════════════════════════════
//    Styles
//    ══════════════════════════════════════════════════════════ */

// const styles = {
//   pageWrap: {
//     maxWidth: 1080,
//     margin: "0 auto",
//     padding: "20px 16px 60px",
//   },
//   loadingWrap: (c) => ({
//     display: "flex",
//     flexDirection: "column",
//     alignItems: "center",
//     justifyContent: "center",
//     minHeight: 340,
//     gap: 12,
//   }),
//   spinner: (c) => ({
//     width: 32,
//     height: 32,
//     border: `3px solid ${c.border}`,
//     borderTopColor: c.btnBg,
//     borderRadius: "50%",
//     animation: "spin 0.9s linear infinite",
//   }),

//   header: {
//     display: "flex",
//     flexWrap: "wrap",
//     alignItems: "flex-end",
//     justifyContent: "space-between",
//     gap: 14,
//     marginBottom: 20,
//   },
//   h1: (c) => ({
//     display: "flex",
//     alignItems: "center",
//     gap: 10,
//     margin: 0,
//     fontSize: "clamp(20px, 3vw, 26px)",
//     fontWeight: 800,
//     letterSpacing: "-0.02em",
//     color: c.heading,
//   }),
//   subtitle: (c) => ({
//     margin: "6px 0 0",
//     fontSize: 13.5,
//     color: c.secondaryText,
//     lineHeight: 1.55,
//     maxWidth: 620,
//   }),

//   contextTabs: (c) => ({
//     display: "grid",
//     gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
//     gap: 10,
//     marginBottom: 16,
//   }),
//   contextTab: (c, active) => ({
//     display: "flex",
//     alignItems: "flex-start",
//     gap: 12,
//     padding: 14,
//     border: `1px solid ${active ? c.btnBg : c.border}`,
//     background: active ? c.hoverBg : c.raisedBg,
//     borderRadius: 12,
//     cursor: "pointer",
//     textAlign: "left",
//     transition: "border-color 0.15s ease, background 0.15s ease",
//     minWidth: 0,
//   }),
//   contextTabIcon: (c, active) => ({
//     width: 32,
//     height: 32,
//     borderRadius: 9,
//     background: active ? c.btnBg : c.hoverBg,
//     color: active ? c.btnText : c.secondaryText,
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     flexShrink: 0,
//   }),

//   toolbar: {
//     display: "flex",
//     alignItems: "center",
//     gap: 12,
//     marginBottom: 14,
//     flexWrap: "wrap",
//   },
//   searchWrap: {
//     position: "relative",
//     flex: "1 1 240px",
//     minWidth: 0,
//   },
//   searchIcon: {
//     position: "absolute",
//     left: 12,
//     top: "50%",
//     transform: "translateY(-50%)",
//     pointerEvents: "none",
//   },
//   searchInput: (c) => ({
//     width: "100%",
//     padding: "9px 34px 9px 36px",
//     borderRadius: 10,
//     border: `1px solid ${c.border}`,
//     background: c.raisedBg,
//     color: c.text,
//     fontSize: 13.5,
//     outline: "none",
//     boxSizing: "border-box",
//   }),
//   searchClear: {
//     position: "absolute",
//     right: 8,
//     top: "50%",
//     transform: "translateY(-50%)",
//     background: "none",
//     border: "none",
//     cursor: "pointer",
//     padding: 4,
//     display: "flex",
//   },
//   resultCount: (c) => ({
//     fontSize: 12.5,
//     color: c.secondaryText,
//     fontWeight: 600,
//   }),

//   listWrap: { display: "grid", gap: 10 },

//   row: (c) => ({
//     background: c.raisedBg,
//     border: `1px solid ${c.border}`,
//     borderRadius: 12,
//     padding: 14,
//     display: "flex",
//     flexDirection: "column",
//     gap: 10,
//   }),
//   rowTop: {
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "space-between",
//     gap: 12,
//   },
//   rowIdentity: {
//     display: "flex",
//     alignItems: "center",
//     gap: 12,
//     minWidth: 0,
//   },
//   thumb: (c) => ({
//     width: 44,
//     height: 44,
//     borderRadius: 10,
//     background: c.hoverBg,
//     overflow: "hidden",
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     flexShrink: 0,
//   }),
//   rowName: (c) => ({
//     fontSize: 14,
//     fontWeight: 700,
//     color: c.heading,
//     display: "inline-flex",
//     alignItems: "center",
//     whiteSpace: "nowrap",
//     overflow: "hidden",
//     textOverflow: "ellipsis",
//     minWidth: 0,
//   }),
//   rowMeta: (c) => ({
//     fontSize: 11.5,
//     color: c.secondaryText,
//     marginTop: 3,
//     whiteSpace: "nowrap",
//     overflow: "hidden",
//     textOverflow: "ellipsis",
//   }),
//   addRowBtn: (c) => ({
//     width: 32,
//     height: 32,
//     borderRadius: 9,
//     border: `1px dashed ${c.border}`,
//     background: "transparent",
//     color: c.secondaryText,
//     cursor: "pointer",
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     flexShrink: 0,
//   }),

//   // ── Chip group (per variant) ─────────────────────────
//   chipGroup: (c) => ({
//     display: "flex",
//     flexDirection: "column",
//     gap: 6,
//     paddingTop: 8,
//     borderTop: `1px solid ${c.separator || c.border}`,
//   }),
//   chipGroupHeader: {
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "space-between",
//     gap: 8,
//   },
//   chipGroupLabel: (c) => ({
//     display: "inline-flex",
//     alignItems: "center",
//     gap: 6,
//     fontSize: 11,
//     fontWeight: 700,
//     letterSpacing: 0.3,
//     textTransform: "uppercase",
//     color: c.secondaryText,
//   }),
//   chipGroupSublabel: (c) => ({
//     fontWeight: 600,
//     color: c.muted,
//     textTransform: "none",
//     letterSpacing: 0,
//   }),
//   chipGroupAdd: (c) => ({
//     width: 22,
//     height: 22,
//     borderRadius: 6,
//     border: "none",
//     background: "transparent",
//     color: c.secondaryText,
//     cursor: "pointer",
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//   }),
//   chipGroupEmpty: (c) => ({
//     fontSize: 11.5,
//     color: c.muted,
//     fontStyle: "italic",
//   }),

//   chipsWrap: {
//     display: "flex",
//     flexWrap: "wrap",
//     gap: 6,
//   },
//   chip: (c, preorder) => ({
//     display: "inline-flex",
//     alignItems: "center",
//     gap: 6,
//     padding: "4px 8px",
//     borderRadius: 999,
//     background: preorder ? c.warningBg : c.successBg,
//     color: preorder ? c.warning : c.success,
//     fontSize: 11.5,
//     fontWeight: 600,
//   }),
//   chipLabel: (c) => ({
//     display: "inline-flex",
//     alignItems: "center",
//     gap: 4,
//     maxWidth: 220,
//     overflow: "hidden",
//     textOverflow: "ellipsis",
//     whiteSpace: "nowrap",
//   }),
//   chipValue: (c) => ({
//     fontWeight: 800,
//     color: c.heading,
//     paddingLeft: 6,
//     borderLeft: `1px solid ${c.separator || c.border}`,
//   }),
//   chipIconBtn: (c, danger) => ({
//     width: 22,
//     height: 22,
//     borderRadius: 6,
//     border: "none",
//     background: "transparent",
//     color: danger ? c.error : "inherit",
//     cursor: "pointer",
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//   }),

//   // Buttons
//   ghostBtn: (c) => ({
//     display: "inline-flex",
//     alignItems: "center",
//     gap: 6,
//     padding: "8px 12px",
//     borderRadius: 9,
//     border: `1px solid ${c.border}`,
//     background: "transparent",
//     color: c.text,
//     cursor: "pointer",
//     fontSize: 12.5,
//     fontWeight: 600,
//   }),
//   primaryBtn: (c, disabled) => ({
//     display: "inline-flex",
//     alignItems: "center",
//     gap: 6,
//     padding: "9px 14px",
//     borderRadius: 9,
//     border: "none",
//     background: disabled ? c.hoverBg : c.btnBg,
//     color: disabled ? c.secondaryText : c.btnText,
//     cursor: disabled ? "not-allowed" : "pointer",
//     fontSize: 12.5,
//     fontWeight: 700,
//   }),
//   secondaryBtn: (c) => ({
//     display: "inline-flex",
//     alignItems: "center",
//     gap: 6,
//     padding: "9px 16px",
//     borderRadius: 9,
//     border: `1px solid ${c.border}`,
//     background: "transparent",
//     color: c.text,
//     cursor: "pointer",
//     fontSize: 13,
//     fontWeight: 600,
//   }),
//   closeBtn: (c) => ({
//     width: 32,
//     height: 32,
//     borderRadius: 8,
//     border: "none",
//     background: c.hoverBg,
//     color: c.text,
//     cursor: "pointer",
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//   }),

//   // Modal
//   modalOverlay: (c) => ({
//     position: "fixed",
//     inset: 0,
//     background: c.scrim || "rgba(0,0,0,0.5)",
//     backdropFilter: "blur(6px)",
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "center",
//     zIndex: 1000,
//     padding: 16,
//     animation: "fadeIn 0.15s ease",
//   }),
//   modalCard: (c) => ({
//     background: c.raisedBg,
//     border: `1px solid ${c.border}`,
//     borderRadius: 16,
//     width: "100%",
//     maxWidth: 480,
//     padding: 24,
//     boxShadow: `0 24px 64px ${c.shadowStrong || c.shadow}`,
//     animation: "slideUp 0.2s ease",
//     boxSizing: "border-box",
//     display: "flex",
//     flexDirection: "column",
//     gap: 18,
//   }),
//   modalHeader: {
//     display: "flex",
//     justifyContent: "space-between",
//     alignItems: "flex-start",
//     gap: 12,
//   },
//   modalTitle: (c) => ({
//     fontSize: 16,
//     fontWeight: 800,
//     color: c.heading,
//     letterSpacing: "-0.01em",
//   }),
//   modalSubtitle: (c) => ({
//     fontSize: 12.5,
//     color: c.secondaryText,
//     marginTop: 3,
//   }),
//   modalBody: {
//     display: "flex",
//     flexDirection: "column",
//     gap: 14,
//   },
//   modalFooter: {
//     display: "flex",
//     justifyContent: "flex-end",
//     gap: 10,
//     marginTop: 4,
//   },

//   fieldLabel: (c) => ({
//     display: "block",
//     fontSize: 12,
//     fontWeight: 700,
//     color: c.text,
//     marginBottom: 6,
//   }),
//   fieldHint: (c) => ({
//     display: "block",
//     fontSize: 11.5,
//     color: c.secondaryText,
//     marginTop: 5,
//   }),
//   fieldError: (c) => ({
//     display: "inline-flex",
//     alignItems: "center",
//     gap: 4,
//     fontSize: 11.5,
//     color: c.error,
//     marginTop: 5,
//   }),
//   input: (c) => ({
//     width: "100%",
//     padding: "10px 12px",
//     borderRadius: 10,
//     border: `1px solid ${c.border}`,
//     background: c.raisedBg,
//     color: c.text,
//     fontSize: 14,
//     outline: "none",
//     boxSizing: "border-box",
//     fontFamily: "inherit",
//   }),
//   select: (c) => ({
//     width: "100%",
//     padding: "10px 12px",
//     borderRadius: 10,
//     border: `1px solid ${c.border}`,
//     background: c.raisedBg,
//     color: c.text,
//     fontSize: 14,
//     outline: "none",
//     boxSizing: "border-box",
//     appearance: "none",
//     fontFamily: "inherit",
//   }),
//   inputPrefixWrap: { position: "relative", display: "flex", alignItems: "center" },
//   inputPrefix: (c) => ({
//     position: "absolute",
//     left: 12,
//     color: c.secondaryText,
//     fontWeight: 700,
//     fontSize: 14,
//     pointerEvents: "none",
//   }),
//   inputWithPrefix: (c) => ({
//     width: "100%",
//     padding: "10px 12px 10px 30px",
//     borderRadius: 10,
//     border: `1px solid ${c.border}`,
//     background: c.raisedBg,
//     color: c.text,
//     fontSize: 14,
//     fontWeight: 700,
//     outline: "none",
//     boxSizing: "border-box",
//     fontFamily: "inherit",
//   }),

//   previewBox: (c) => ({
//     background: c.mainBg,
//     borderRadius: 10,
//     padding: 12,
//     display: "flex",
//     flexDirection: "column",
//     gap: 4,
//   }),
//   previewLabel: (c) => ({
//     fontSize: 11,
//     fontWeight: 700,
//     letterSpacing: 0.3,
//     textTransform: "uppercase",
//     color: c.secondaryText,
//   }),
//   previewValue: (c) => ({
//     fontSize: 14,
//     fontWeight: 800,
//     color: c.heading,
//   }),
//   previewHint: (c) => ({
//     fontSize: 11.5,
//     color: c.secondaryText,
//   }),

//   errorBox: (c) => ({
//     display: "flex",
//     alignItems: "center",
//     gap: 8,
//     padding: "10px 12px",
//     borderRadius: 10,
//     background: c.errorBg,
//     color: c.error,
//     fontSize: 12.5,
//     fontWeight: 600,
//   }),

//   emptyState: (c) => ({
//     padding: "48px 24px",
//     textAlign: "center",
//     border: `1px dashed ${c.border}`,
//     borderRadius: 14,
//     background: c.raisedBg,
//   }),
//   emptyIcon: (c) => ({
//     width: 56,
//     height: 56,
//     borderRadius: 16,
//     background: c.hoverBg,
//     color: c.accent || c.btnBg,
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     marginBottom: 12,
//   }),
//   emptyTitle: (c) => ({
//     margin: "0 0 6px",
//     fontSize: 16,
//     fontWeight: 800,
//     color: c.heading,
//   }),
//   emptyText: (c) => ({
//     margin: 0,
//     fontSize: 13,
//     color: c.secondaryText,
//     maxWidth: 380,
//     marginLeft: "auto",
//     marginRight: "auto",
//     lineHeight: 1.55,
//   }),
// };

































// screens/marketplace/MarketplacePricing.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  SlidersHorizontal,
  Plus,
  Trash2,
  Pencil,
  X,
  Package as PackageIcon,
  Users,
  ShoppingBag,
  Clock,
  AlertCircle,
  Info,
  Save,
  Search,
  RefreshCw,
  Layers,
  Globe,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../providers/AuthProvider";
import { useTheme } from "../../hooks/useTheme";
import { useToast } from "../../providers/ToastProvider";
import {
  PRICE_TYPES,
  availablePriceTypes,
  priceTypeMeta,
  variantKeyLabel,
  listPricing,
  savePricing,
  updatePricing,
  deletePricing,
} from "./pricingApi";

/* ══════════════════════════════════════════════════════════
   Pricing workspace for agents and sub-agents.

   Two contexts, driven by the backend's price-type matrix:
     • "customer"  → what walk-in buyers pay on my storefront
     • "subAgent"  → what my sub-agents pay as their cost
                    (agent-only; sub-agents can't set their own cost)

   Variant awareness:
     • Each product row groups its overrides by variant_key.
     • "All variants" (variant_key === "") shows first, then one row
       per variant that has an override.
     • The modal lets you pick a scope — product-level or a specific
       variant — and remembers the choice.

   Because POST /pricing/ does update-or-create keyed on
   (product, variant_key, user, price_type), the form never needs
   to know in advance whether a row exists — it just posts.
   ══════════════════════════════════════════════════════════ */

const CONTEXTS = [
  {
    id: "customer",
    title: "My customers",
    description: "Prices your walk-in buyers see on your storefront.",
    icon: ShoppingBag,
  },
  {
    id: "subAgent",
    title: "My sub-agents",
    description: "Costs your sub-agents see when they resell.",
    icon: Users,
  },
];

export default function MarketplacePricing() {
  const { authFetch, user } = useAuth();
  const { colors } = useTheme();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const role = user?.role || "AGENT";
  const canSeeSubAgentContext = role === "ADMIN" || role === "AGENT";

  const [context, setContext] = useState("customer");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [products, setProducts] = useState([]);
  const [subAgents, setSubAgents] = useState([]);
  const [overrides, setOverrides] = useState([]);

  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null); // { mode, row?, context, productId? }
  const [busyId, setBusyId] = useState(null);

  // ── Load ────────────────────────────────────────────────
  const load = useCallback(
    async ({ silent } = {}) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      try {
        const fetches = [
          authFetch("/marketplace/products/"),
          authFetch("/marketplace/pricing/"),
        ];
        if (canSeeSubAgentContext) {
          fetches.push(authFetch("/accounts/agent/sub-agents/"));
        }
        const results = await Promise.all(fetches);
        const bodies = await Promise.all(results.map((r) => r.json()));

        if (results[0].ok) {
          setProducts(
            (bodies[0].data || []).map((p) => ({
              id: p.id,
              name: p.name,
              sku: p.sku || p.slug,
              basePriceAgent: parseFloat(p.base_price_agent || 0),
              basePriceCustomer: parseFloat(p.base_price_customer || 0),
              preOrderBaseAgent: parseFloat(
                p.pre_order_price_agent || p.base_price_agent || 0
              ),
              preOrderBaseCustomer: parseFloat(
                p.pre_order_price_customer || p.base_price_customer || 0
              ),
              image:
                p.thumbnail_image_url ||
                p.main_image_url ||
                p.main_image ||
                null,
              saleType: p.sale_type,
              hasVariants: !!p.has_variants,
              variantAttributes: Array.isArray(p.variant_attributes)
                ? p.variant_attributes
                : [],
              variants: Array.isArray(p.variants)
                ? p.variants.map((v) => ({
                    key: v.key,
                    attributes: v.attributes || {},
                    stock: v.stock != null ? Number(v.stock) : null,
                    basePriceAgent: parseFloat(v.base_price_agent || 0),
                    basePriceCustomer: parseFloat(
                      v.base_price_customer || 0
                    ),
                    preOrderBaseAgent: parseFloat(
                      v.pre_order_price_agent || v.base_price_agent || 0
                    ),
                    preOrderBaseCustomer: parseFloat(
                      v.pre_order_price_customer ||
                        v.base_price_customer ||
                        0
                    ),
                  }))
                : [],
            }))
          );
        }
        if (results[1].ok) setOverrides(bodies[1].data || []);
        if (canSeeSubAgentContext && results[2]?.ok) {
          setSubAgents(bodies[2].data?.sub_agents || []);
        }
      } catch (err) {
        console.error(err);
        showToast("Could not load pricing", "error");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authFetch, canSeeSubAgentContext, showToast]
  );

  useEffect(() => {
    load();
  }, [load]);

  // ── Derived ─────────────────────────────────────────────
  const overridesByProduct = useMemo(() => {
    const map = new Map();
    overrides.forEach((o) => {
      if (!map.has(o.product)) map.set(o.product, []);
      map.get(o.product).push(o);
    });
    return map;
  }, [overrides]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? products.filter((p) => p.name.toLowerCase().includes(q))
      : products;

    return filtered.map((product) => {
      const productOverrides = overridesByProduct.get(product.id) || [];
      return { product, overrides: productOverrides };
    });
  }, [products, overridesByProduct, search]);

  // ── Actions ─────────────────────────────────────────────
  const handleSave = async (payload) => {
    try {
      const saved = await savePricing(authFetch, payload);
      // Optimistic merge keyed on (product, variant_key, user, price_type)
      setOverrides((prev) => {
        const key = (o) =>
          `${o.product}:${o.variant_key || ""}:${o.user}:${o.price_type}`;
        const savedKey = key(saved);
        const without = prev.filter((o) => key(o) !== savedKey);
        return [...without, saved];
      });
      showToast("Price saved", "success");
      setModal(null);
    } catch (err) {
      showToast(err.message, "error");
      throw err;
    }
  };

  const handleDelete = async (row) => {
    const scope = row.variant_key
      ? `the ${variantKeyLabel(row.variant_key)} variant`
      : "all variants";
    if (
      !window.confirm(
        `Remove the ${priceTypeMeta(row.price_type).label} override for ${scope}? The fallback price will apply.`
      )
    )
      return;
    setBusyId(row.id);
    try {
      await deletePricing(authFetch, row.id);
      setOverrides((prev) => prev.filter((o) => o.id !== row.id));
      showToast("Override removed", "success");
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setBusyId(null);
    }
  };

  // ── Render ──────────────────────────────────────────────
  if (loading) {
    return (
      <div style={styles.loadingWrap(colors)}>
        <div style={styles.spinner(colors)} />
        <span style={{ fontSize: 13, color: colors.secondaryText }}>
          Loading pricing workspace…
        </span>
      </div>
    );
  }

  return (
    <div style={styles.pageWrap}>
      {/* Header */}
      <header style={styles.header}>
        <div style={{ minWidth: 0 }}>
          <h1 style={styles.h1(colors)}>
            <SlidersHorizontal size={22} color={colors.btnBg} />
            Store pricing
          </h1>
          <p style={styles.subtitle(colors)}>
            Control what your customers and sub-agents pay. Every price falls
            back to the admin base if you don't set an override.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={() => load({ silent: true })}
            disabled={refreshing}
            style={styles.ghostBtn(colors)}
          >
            <RefreshCw
              size={14}
              style={{
                animation: refreshing ? "spin 1s linear infinite" : "none",
              }}
            />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setModal({ mode: "create", context })}
            style={styles.primaryBtn(colors, false)}
          >
            <Plus size={14} />
            Add price
          </button>
        </div>
      </header>

      {/* Context tabs */}
      {canSeeSubAgentContext && (
        <div style={styles.contextTabs(colors)}>
          {CONTEXTS.map((c) => {
            const Icon = c.icon;
            const active = context === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setContext(c.id)}
                style={styles.contextTab(colors, active)}
              >
                <span style={styles.contextTabIcon(colors, active)}>
                  <Icon size={15} />
                </span>
                <span style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
                  <span
                    style={{
                      display: "block",
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: active ? colors.heading : colors.text,
                    }}
                  >
                    {c.title}
                  </span>
                  <span
                    style={{
                      display: "block",
                      fontSize: 11.5,
                      color: colors.secondaryText,
                      marginTop: 2,
                    }}
                  >
                    {c.description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Search */}
      <div style={styles.toolbar}>
        <div style={styles.searchWrap}>
          <Search size={14} color={colors.secondaryText} style={styles.searchIcon} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products…"
            style={styles.searchInput(colors)}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              style={styles.searchClear}
              aria-label="Clear"
            >
              <X size={13} />
            </button>
          )}
        </div>
        <span style={styles.resultCount(colors)}>
          {rows.length} product{rows.length === 1 ? "" : "s"}
        </span>
      </div>

      {/* Rows */}
      {rows.length === 0 ? (
        <EmptyState colors={colors} search={search} />
      ) : (
        <div style={styles.listWrap}>
          {rows.map(({ product, overrides }) => (
            <ProductPricingRow
              key={product.id}
              product={product}
              overrides={overrides}
              context={context}
              role={role}
              subAgents={subAgents}
              colors={colors}
              onAdd={(variantKey) =>
                setModal({
                  mode: "create",
                  context,
                  productId: product.id,
                  variantKey,
                })
              }
              onEdit={(row) => setModal({ mode: "edit", row, context })}
              onDelete={handleDelete}
              busyId={busyId}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <PriceEditorModal
          mode={modal.mode}
          initial={modal.row}
          context={modal.context}
          role={role}
          user={user}
          products={products}
          subAgents={subAgents}
          presetProductId={modal.productId}
          presetVariantKey={modal.variantKey}
          colors={colors}
          onClose={() => setModal(null)}
          onSave={handleSave}
          showToast={showToast}
        />
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Product row — grouped by variant
   ══════════════════════════════════════════════════════════ */

const ProductPricingRow = ({
  product,
  overrides,
  context,
  role,
  subAgents,
  colors,
  onAdd,
  onEdit,
  onDelete,
  busyId,
}) => {
  // Filter overrides by context (customer-facing vs sub-agent cost)
  const contextOverrides = overrides.filter((o) => {
    const meta = priceTypeMeta(o.price_type);
    if (context === "customer") return meta.scope === "customer-facing";
    return (
      meta.scope === "sub-agent-cost" ||
      meta.scope === "sub-agent-cost-override"
    );
  });

  // Group by variant key. "" is the product-level bucket.
  const byVariant = useMemo(() => {
    const map = new Map();
    contextOverrides.forEach((o) => {
      const key = o.variant_key || "";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(o);
    });
    return map;
  }, [contextOverrides]);

  const hasVariants = product.hasVariants && product.variants.length > 0;

  // Product-level summary line
  const productLevel = byVariant.get("") || [];
  const customerPriceAtProduct = productLevel.find(
    (o) => o.price_type === "AGENT_CUSTOMER_PRICE"
  );
  const subBaseAtProduct = productLevel.find(
    (o) => o.price_type === "AGENT_SUB_BASE_PRICE"
  );

  // Variant keys to display as sub-rows
  const variantKeysToShow = useMemo(() => {
    if (!hasVariants) return [];
    // Union of: variants defined on the product + variant keys with overrides
    const defined = product.variants.map((v) => v.key);
    const withOverrides = [...byVariant.keys()].filter((k) => k !== "");
    const seen = new Set();
    return [...defined, ...withOverrides].filter((k) => {
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [hasVariants, product.variants, byVariant]);

  return (
    <div style={styles.row(colors)}>
      <div style={styles.rowTop}>
        <div style={styles.rowIdentity}>
          <div style={styles.thumb(colors)}>
            {product.image ? (
              <img
                src={product.image}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <PackageIcon size={20} color={colors.secondaryText} />
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={styles.rowName(colors)}>
              {product.name}
              {hasVariants && (
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "2px 7px",
                    borderRadius: 999,
                    background: colors.hoverBg,
                    color: colors.secondaryText,
                    letterSpacing: 0.3,
                    textTransform: "uppercase",
                  }}
                >
                  {product.variants.length} variants
                </span>
              )}
            </div>
            <div style={styles.rowMeta(colors)}>
              Base agent ₵{product.basePriceAgent.toFixed(2)}
              {context === "customer" && customerPriceAtProduct && (
                <>
                  {" · "}
                  Customer ₵
                  {parseFloat(customerPriceAtProduct.price).toFixed(2)}
                </>
              )}
              {context === "subAgent" && subBaseAtProduct && (
                <>
                  {" · "}
                  Sub-agent base ₵
                  {parseFloat(subBaseAtProduct.price).toFixed(2)}
                </>
              )}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onAdd("")}
          style={styles.addRowBtn(colors)}
          title="Add override"
        >
          <Plus size={14} />
        </button>
      </div>

      {/* Product-level overrides */}
      {productLevel.length > 0 && (
        <ChipGroup
          label="All variants"
          icon={Globe}
          overrides={productLevel}
          subAgents={subAgents}
          colors={colors}
          onEdit={onEdit}
          onDelete={onDelete}
          busyId={busyId}
        />
      )}

      {/* Per-variant overrides */}
      {hasVariants &&
        variantKeysToShow.map((vKey) => {
          const vOverrides = byVariant.get(vKey) || [];
          const variantMeta = product.variants.find((v) => v.key === vKey);
          return (
            <ChipGroup
              key={vKey}
              label={variantKeyLabel(vKey)}
              sublabel={
                variantMeta
                  ? `base ₵${variantMeta.basePriceAgent.toFixed(2)}`
                  : null
              }
              icon={Layers}
              overrides={vOverrides}
              subAgents={subAgents}
              colors={colors}
              onAdd={() => onAdd(vKey)}
              onEdit={onEdit}
              onDelete={onDelete}
              busyId={busyId}
            />
          );
        })}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Chip group — one row per variant scope
   ══════════════════════════════════════════════════════════ */

const ChipGroup = ({
  label,
  sublabel,
  icon: Icon,
  overrides,
  subAgents,
  colors,
  onAdd,
  onEdit,
  onDelete,
  busyId,
}) => {
  return (
    <div style={styles.chipGroup(colors)}>
      <div style={styles.chipGroupHeader}>
        <div style={styles.chipGroupLabel(colors)}>
          {Icon && <Icon size={11} />}
          {label}
          {sublabel && (
            <span style={styles.chipGroupSublabel(colors)}> · {sublabel}</span>
          )}
        </div>
        {onAdd && (
          <button
            type="button"
            onClick={onAdd}
            style={styles.chipGroupAdd(colors)}
            title="Add override for this variant"
          >
            <Plus size={11} />
          </button>
        )}
      </div>
      {overrides.length === 0 ? (
        <span style={styles.chipGroupEmpty(colors)}>No override</span>
      ) : (
        <div style={styles.chipsWrap}>
          {overrides.map((o) => {
            const meta = priceTypeMeta(o.price_type);
            const subName =
              meta.scope === "sub-agent-cost-override"
                ? subAgents.find((s) => s.user_id === o.user)?.username ||
                  o.username
                : null;
            const busy = busyId === o.id;
            return (
              <span key={o.id} style={styles.chip(colors, meta.isPreorder)}>
                <span style={styles.chipLabel(colors)}>
                  {meta.isPreorder ? <Clock size={11} /> : <Info size={11} />}
                  {subName ? `${meta.label} · @${subName}` : meta.label}
                </span>
                <span style={styles.chipValue(colors)}>
                  ₵{parseFloat(o.price).toFixed(2)}
                </span>
                <button
                  type="button"
                  onClick={() => onEdit(o)}
                  disabled={busy}
                  style={styles.chipIconBtn(colors)}
                  title="Edit"
                >
                  <Pencil size={11} />
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(o)}
                  disabled={busy}
                  style={styles.chipIconBtn(colors, true)}
                  title="Remove"
                >
                  {busy ? (
                    <RefreshCw
                      size={11}
                      style={{ animation: "spin 0.9s linear infinite" }}
                    />
                  ) : (
                    <Trash2 size={11} />
                  )}
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Price editor modal
   ══════════════════════════════════════════════════════════ */

const PriceEditorModal = ({
  mode,
  initial,
  context,
  role,
  user,
  products,
  subAgents,
  presetProductId,
  presetVariantKey,
  colors,
  onClose,
  onSave,
}) => {
  const isEdit = mode === "edit";

  const availableTypes = useMemo(
    () => availablePriceTypes(role, context),
    [role, context]
  );

  const [product, setProduct] = useState(
    initial?.product || presetProductId || ""
  );
  const [variantKey, setVariantKey] = useState(
    initial?.variant_key || presetVariantKey || ""
  );
  const [targetUser, setTargetUser] = useState(initial?.user || "");
  const [priceType, setPriceType] = useState(
    initial?.price_type || availableTypes[0]?.value || ""
  );
  const [price, setPrice] = useState(initial?.price || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const selectedProduct = products.find((p) => p.id === product);
  const productVariants = selectedProduct?.variants || [];

  // The sub-agent picker is only relevant when the price type targets
  // a specific sub-agent (AGENT_SUB_CUSTOM_*), and only when the caller
  // is an agent or admin.
  const needsSubAgentPicker =
    !isEdit &&
    (priceType === PRICE_TYPES.AGENT_SUB_CUSTOM_PRICE.value ||
      priceType ===
        PRICE_TYPES.AGENT_SUB_PRE_ORDER_CUSTOM_PRICE.value) &&
    (role === "AGENT" || role === "ADMIN");

  // Fallback price shown to the user. Uses variant base if a variant
  // is picked, else the product-level base.
  const fallbackPrice = useMemo(() => {
    if (!selectedProduct) return null;
    const isPreorder = priceTypeMeta(priceType).isPreorder;
    if (variantKey) {
      const v = productVariants.find((x) => x.key === variantKey);
      if (v) {
        return isPreorder ? v.preOrderBaseAgent : v.basePriceAgent;
      }
    }
    return isPreorder
      ? selectedProduct.preOrderBaseAgent
      : selectedProduct.basePriceAgent;
  }, [selectedProduct, productVariants, variantKey, priceType]);

  const submit = async (e) => {
    e.preventDefault();
    setFieldErrors({});

    if (!product) {
      setFieldErrors({ product: "Choose a product" });
      return;
    }
    if (!price || Number(price) <= 0) {
      setFieldErrors({ price: "Enter a valid price" });
      return;
    }

    const payload = {
      product,
      variant_key: variantKey || "",
      price_type: priceType,
      price,
      notes,
    };

    if (!isEdit) {
      // The backend expects `user` on every pricing row. Two cases:
      //   • Pricing a specific sub-agent → user = sub-agent's user_id
      //   • Self-targeted pricing → user = caller's own user id
      if (needsSubAgentPicker) {
        if (!targetUser) {
          setFieldErrors({ targetUser: "Select a sub-agent" });
          return;
        }
        payload.user = targetUser;
      } else {
        payload.user = user.id;
      }
    }

    setSubmitting(true);
    try {
      await onSave(payload);
    } catch (err) {
      setFieldErrors({ _general: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={styles.modalOverlay(colors)}
    >
      <form onSubmit={submit} style={styles.modalCard(colors)}>
        <header style={styles.modalHeader}>
          <div>
            <div style={styles.modalTitle(colors)}>
              {isEdit ? "Edit price override" : "Add price override"}
            </div>
            <div style={styles.modalSubtitle(colors)}>
              {context === "customer"
                ? "What your customers pay"
                : "What your sub-agents pay"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={styles.closeBtn(colors)}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </header>

        <div style={styles.modalBody}>
          {!isEdit && (
            <Field label="Product" colors={colors} error={fieldErrors.product}>
              <select
                required
                value={product}
                onChange={(e) => {
                  setProduct(e.target.value);
                  setVariantKey("");
                }}
                style={styles.select(colors)}
              >
                <option value="">Choose product</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {isEdit && selectedProduct ? (
            <div style={styles.previewBox(colors)}>
              <div style={styles.previewLabel(colors)}>Product</div>
              <div style={styles.previewValue(colors)}>
                {initial.product_name || selectedProduct.name}
              </div>
            </div>
          ) : null}

          {/* Variant scope picker */}
          {!isEdit && selectedProduct && productVariants.length > 0 && (
            <Field
              label="Applies to"
              colors={colors}
              hint="Product-level overrides apply when a variant has no override of its own."
            >
              <select
                value={variantKey}
                onChange={(e) => setVariantKey(e.target.value)}
                style={styles.select(colors)}
              >
                <option value="">All variants (product-level)</option>
                {productVariants.map((v) => (
                  <option key={v.key} value={v.key}>
                    {variantKeyLabel(v.key)}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {isEdit && initial?.variant_key && (
            <div style={styles.previewBox(colors)}>
              <div style={styles.previewLabel(colors)}>Applies to</div>
              <div style={styles.previewValue(colors)}>
                {variantKeyLabel(initial.variant_key)}
              </div>
            </div>
          )}

          <Field label="Price type" colors={colors}>
            <select
              required
              disabled={isEdit}
              value={priceType}
              onChange={(e) => setPriceType(e.target.value)}
              style={styles.select(colors)}
            >
              {availableTypes.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </Field>

          {needsSubAgentPicker && (
            <Field
              label="Target sub-agent"
              colors={colors}
              error={fieldErrors.targetUser}
            >
              <select
                required
                value={targetUser}
                onChange={(e) => setTargetUser(e.target.value)}
                style={styles.select(colors)}
              >
                <option value="">Choose sub-agent</option>
                {subAgents.map((s) => (
                  <option key={s.user_id} value={s.user_id}>
                    {s.username || s.full_name || s.user_id}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {fallbackPrice != null && (
            <div style={styles.previewBox(colors)}>
              <div style={styles.previewLabel(colors)}>
                Fallback price if left empty
              </div>
              <div style={styles.previewValue(colors)}>
                ₵{fallbackPrice.toFixed(2)}
              </div>
              <div style={styles.previewHint(colors)}>
                Your price must be at least this much.
              </div>
            </div>
          )}

          <Field
            label="Your price"
            colors={colors}
            error={fieldErrors.price}
          >
            <div style={styles.inputPrefixWrap}>
              <span style={styles.inputPrefix(colors)}>₵</span>
              <input
                type="number"
                required
                step="0.01"
                min="0"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                style={styles.inputWithPrefix(colors)}
              />
            </div>
          </Field>

          <Field label="Notes (optional)" colors={colors}>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why this price?"
              style={styles.input(colors)}
            />
          </Field>

          {fieldErrors._general && (
            <div style={styles.errorBox(colors)}>
              <AlertCircle size={14} />
              {fieldErrors._general}
            </div>
          )}
        </div>

        <footer style={styles.modalFooter}>
          <button
            type="button"
            onClick={onClose}
            style={styles.secondaryBtn(colors)}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            style={styles.primaryBtn(colors, submitting)}
          >
            {submitting ? (
              <>
                <RefreshCw
                  size={13}
                  style={{ animation: "spin 0.9s linear infinite" }}
                />
                Saving…
              </>
            ) : (
              <>
                <Save size={14} /> Save
              </>
            )}
          </button>
        </footer>
      </form>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Small components
   ══════════════════════════════════════════════════════════ */

const Field = ({ label, children, colors, error, hint }) => (
  <label style={{ display: "block" }}>
    <span style={styles.fieldLabel(colors)}>{label}</span>
    {children}
    {hint && !error && (
      <span style={styles.fieldHint(colors)}>{hint}</span>
    )}
    {error && (
      <span style={styles.fieldError(colors)}>
        <AlertCircle size={11} /> {error}
      </span>
    )}
  </label>
);

const EmptyState = ({ colors, search }) => (
  <div style={styles.emptyState(colors)}>
    <div style={styles.emptyIcon(colors)}>
      <SlidersHorizontal size={24} />
    </div>
    <h3 style={styles.emptyTitle(colors)}>
      {search ? "No matches" : "No products yet"}
    </h3>
    <p style={styles.emptyText(colors)}>
      {search
        ? "Try a different search term."
        : "Products will appear here once the catalogue is populated."}
    </p>
  </div>
);

/* ══════════════════════════════════════════════════════════
   Styles
   ══════════════════════════════════════════════════════════ */

const styles = {
  pageWrap: {
    maxWidth: 1080,
    margin: "0 auto",
    padding: "20px 16px 60px",
  },
  loadingWrap: (c) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 340,
    gap: 12,
  }),
  spinner: (c) => ({
    width: 32,
    height: 32,
    border: `3px solid ${c.border}`,
    borderTopColor: c.btnBg,
    borderRadius: "50%",
    animation: "spin 0.9s linear infinite",
  }),

  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 14,
    marginBottom: 20,
  },
  h1: (c) => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    margin: 0,
    fontSize: "clamp(20px, 3vw, 26px)",
    fontWeight: 800,
    letterSpacing: "-0.02em",
    color: c.heading,
  }),
  subtitle: (c) => ({
    margin: "6px 0 0",
    fontSize: 13.5,
    color: c.secondaryText,
    lineHeight: 1.55,
    maxWidth: 620,
  }),

  contextTabs: (c) => ({
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 10,
    marginBottom: 16,
  }),
  contextTab: (c, active) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: 14,
    border: `1px solid ${active ? c.btnBg : c.border}`,
    background: active ? c.hoverBg : c.raisedBg,
    borderRadius: 12,
    cursor: "pointer",
    textAlign: "left",
    transition: "border-color 0.15s ease, background 0.15s ease",
    minWidth: 0,
  }),
  contextTabIcon: (c, active) => ({
    width: 32,
    height: 32,
    borderRadius: 9,
    background: active ? c.btnBg : c.hoverBg,
    color: active ? c.btnText : c.secondaryText,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),

  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 14,
    flexWrap: "wrap",
  },
  searchWrap: {
    position: "relative",
    flex: "1 1 240px",
    minWidth: 0,
  },
  searchIcon: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
  },
  searchInput: (c) => ({
    width: "100%",
    padding: "9px 34px 9px 36px",
    borderRadius: 10,
    border: `1px solid ${c.border}`,
    background: c.raisedBg,
    color: c.text,
    fontSize: 13.5,
    outline: "none",
    boxSizing: "border-box",
  }),
  searchClear: {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 4,
    display: "flex",
  },
  resultCount: (c) => ({
    fontSize: 12.5,
    color: c.secondaryText,
    fontWeight: 600,
  }),

  listWrap: { display: "grid", gap: 10 },

  row: (c) => ({
    background: c.raisedBg,
    border: `1px solid ${c.border}`,
    borderRadius: 12,
    padding: 14,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  }),
  rowTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  rowIdentity: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },
  thumb: (c) => ({
    width: 44,
    height: 44,
    borderRadius: 10,
    background: c.hoverBg,
    overflow: "hidden",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),
  rowName: (c) => ({
    fontSize: 14,
    fontWeight: 700,
    color: c.heading,
    display: "inline-flex",
    alignItems: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  }),
  rowMeta: (c) => ({
    fontSize: 11.5,
    color: c.secondaryText,
    marginTop: 3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  addRowBtn: (c) => ({
    width: 32,
    height: 32,
    borderRadius: 9,
    border: `1px dashed ${c.border}`,
    background: "transparent",
    color: c.secondaryText,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),

  // ── Chip group (per variant) ─────────────────────────
  chipGroup: (c) => ({
    display: "flex",
    flexDirection: "column",
    gap: 6,
    paddingTop: 8,
    borderTop: `1px solid ${c.separator || c.border}`,
  }),
  chipGroupHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  chipGroupLabel: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    color: c.secondaryText,
  }),
  chipGroupSublabel: (c) => ({
    fontWeight: 600,
    color: c.muted,
    textTransform: "none",
    letterSpacing: 0,
  }),
  chipGroupAdd: (c) => ({
    width: 22,
    height: 22,
    borderRadius: 6,
    border: "none",
    background: "transparent",
    color: c.secondaryText,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  chipGroupEmpty: (c) => ({
    fontSize: 11.5,
    color: c.muted,
    fontStyle: "italic",
  }),

  chipsWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  chip: (c, preorder) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 8px",
    borderRadius: 999,
    background: preorder ? c.warningBg : c.successBg,
    color: preorder ? c.warning : c.success,
    fontSize: 11.5,
    fontWeight: 600,
  }),
  chipLabel: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
  chipValue: (c) => ({
    fontWeight: 800,
    color: c.heading,
    paddingLeft: 6,
    borderLeft: `1px solid ${c.separator || c.border}`,
  }),
  chipIconBtn: (c, danger) => ({
    width: 22,
    height: 22,
    borderRadius: 6,
    border: "none",
    background: "transparent",
    color: danger ? c.error : "inherit",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  }),

  // Buttons
  ghostBtn: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 12px",
    borderRadius: 9,
    border: `1px solid ${c.border}`,
    background: "transparent",
    color: c.text,
    cursor: "pointer",
    fontSize: 12.5,
    fontWeight: 600,
  }),
  primaryBtn: (c, disabled) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "9px 14px",
    borderRadius: 9,
    border: "none",
    background: disabled ? c.hoverBg : c.btnBg,
    color: disabled ? c.secondaryText : c.btnText,
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: 12.5,
    fontWeight: 700,
  }),
  secondaryBtn: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "9px 16px",
    borderRadius: 9,
    border: `1px solid ${c.border}`,
    background: "transparent",
    color: c.text,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
  }),
  closeBtn: (c) => ({
    width: 32,
    height: 32,
    borderRadius: 8,
    border: "none",
    background: c.hoverBg,
    color: c.text,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  }),

  // Modal
  modalOverlay: (c) => ({
    position: "fixed",
    inset: 0,
    background: c.scrim || "rgba(0,0,0,0.5)",
    backdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 16,
    animation: "fadeIn 0.15s ease",
  }),
  modalCard: (c) => ({
    background: c.raisedBg,
    border: `1px solid ${c.border}`,
    borderRadius: 16,
    width: "100%",
    maxWidth: 480,
    padding: 24,
    boxShadow: `0 24px 64px ${c.shadowStrong || c.shadow}`,
    animation: "slideUp 0.2s ease",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: 18,
  }),
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
  },
  modalTitle: (c) => ({
    fontSize: 16,
    fontWeight: 800,
    color: c.heading,
    letterSpacing: "-0.01em",
  }),
  modalSubtitle: (c) => ({
    fontSize: 12.5,
    color: c.secondaryText,
    marginTop: 3,
  }),
  modalBody: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 4,
  },

  fieldLabel: (c) => ({
    display: "block",
    fontSize: 12,
    fontWeight: 700,
    color: c.text,
    marginBottom: 6,
  }),
  fieldHint: (c) => ({
    display: "block",
    fontSize: 11.5,
    color: c.secondaryText,
    marginTop: 5,
  }),
  fieldError: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11.5,
    color: c.error,
    marginTop: 5,
  }),
  input: (c) => ({
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${c.border}`,
    background: c.raisedBg,
    color: c.text,
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
  }),
  select: (c) => ({
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${c.border}`,
    background: c.raisedBg,
    color: c.text,
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    appearance: "none",
    fontFamily: "inherit",
  }),
  inputPrefixWrap: { position: "relative", display: "flex", alignItems: "center" },
  inputPrefix: (c) => ({
    position: "absolute",
    left: 12,
    color: c.secondaryText,
    fontWeight: 700,
    fontSize: 14,
    pointerEvents: "none",
  }),
  inputWithPrefix: (c) => ({
    width: "100%",
    padding: "10px 12px 10px 30px",
    borderRadius: 10,
    border: `1px solid ${c.border}`,
    background: c.raisedBg,
    color: c.text,
    fontSize: 14,
    fontWeight: 700,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
  }),

  previewBox: (c) => ({
    background: c.mainBg,
    borderRadius: 10,
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  }),
  previewLabel: (c) => ({
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    color: c.secondaryText,
  }),
  previewValue: (c) => ({
    fontSize: 14,
    fontWeight: 800,
    color: c.heading,
  }),
  previewHint: (c) => ({
    fontSize: 11.5,
    color: c.secondaryText,
  }),

  errorBox: (c) => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    borderRadius: 10,
    background: c.errorBg,
    color: c.error,
    fontSize: 12.5,
    fontWeight: 600,
  }),

  emptyState: (c) => ({
    padding: "48px 24px",
    textAlign: "center",
    border: `1px dashed ${c.border}`,
    borderRadius: 14,
    background: c.raisedBg,
  }),
  emptyIcon: (c) => ({
    width: 56,
    height: 56,
    borderRadius: 16,
    background: c.hoverBg,
    color: c.accent || c.btnBg,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 12,
  }),
  emptyTitle: (c) => ({
    margin: "0 0 6px",
    fontSize: 16,
    fontWeight: 800,
    color: c.heading,
  }),
  emptyText: (c) => ({
    margin: 0,
    fontSize: 13,
    color: c.secondaryText,
    maxWidth: 380,
    marginLeft: "auto",
    marginRight: "auto",
    lineHeight: 1.55,
  }),
};